import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Share2 } from "lucide-react";

export default function RSVPFormRenderer({ event, accent = "#1A1A1A", onSubmitted, refToken = null }) {
  const [form, setForm] = useState({
    name: "", email: "", attending: "yes", guest_count: 1, dietary: "", message: "",
  });
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // null | { share_token, name }

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAns = (qid, v) => setAnswers((a) => ({ ...a, [qid]: v }));
  const toggleCheckbox = (qid, opt) => {
    const cur = Array.isArray(answers[qid]) ? answers[qid] : [];
    setAns(qid, cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt]);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Name and email are required");
    for (const q of event.custom_questions || []) {
      if (q.required) {
        const v = answers[q.id];
        if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) {
          return toast.error(`Please answer: ${q.label}`);
        }
      }
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/events/${event.slug}/rsvps`, {
        ...form,
        guest_count: parseInt(form.guest_count) || 1,
        answers,
        ref: refToken,
      });
      setSubmitted({ share_token: data.share_token, name: form.name });
      toast.success("RSVP received — thank you!");
      onSubmitted && onSubmitted();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    const shareUrl = submitted.share_token && event.allow_guest_invites
      ? `${window.location.origin}/i/${event.slug}?ref=${submitted.share_token}`
      : null;
    const copy = () => { navigator.clipboard.writeText(shareUrl); toast.success("Personal invite link copied"); };
    const share = async () => {
      if (navigator.share) {
        try {
          await navigator.share({
            title: event.title,
            text: `${submitted.name} invited you to ${event.title}`,
            url: shareUrl,
          });
        } catch {}
      } else { copy(); }
    };
    return (
      <div data-testid="rsvp-thank-you" className="text-center py-10">
        <div style={{ fontFamily: "'Cormorant Garamond', serif", color: accent }} className="text-4xl sm:text-5xl font-light">
          Thank you.
        </div>
        <p className="mt-3 text-neutral-600">Your RSVP has been received. We can&apos;t wait to see you.</p>
        {shareUrl && (
          <div data-testid="rsvp-share-block" className="mt-10 border pt-8" style={{ borderColor: `${accent}22` }}>
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-70 mb-2">Bring a friend</div>
            <p style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-2xl font-light px-4 mb-4">
              Know someone else who should be there? Share your personal invite link.
            </p>
            <div className="max-w-md mx-auto border p-3 bg-white/60 text-xs break-all mt-3" style={{ borderColor: `${accent}22` }} data-testid="rsvp-share-url">
              {shareUrl}
            </div>
            <div className="mt-4 flex justify-center gap-2">
              <Button onClick={copy} variant="outline" data-testid="rsvp-share-copy" className="rounded-sm">
                <Copy size={14} className="mr-2" /> Copy link
              </Button>
              <Button onClick={share} data-testid="rsvp-share-native" className="rounded-sm" style={{ background: accent, color: "white" }}>
                <Share2 size={14} className="mr-2" /> Share
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="rsvp-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-[10px] tracking-[0.25em] uppercase">Your name</Label>
          <Input data-testid="rsvp-name" required value={form.name} onChange={(e) => setField("name", e.target.value)} className="rounded-sm mt-1" />
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.25em] uppercase">Email</Label>
          <Input data-testid="rsvp-email" required type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} className="rounded-sm mt-1" />
        </div>
      </div>

      <div>
        <Label className="text-[10px] tracking-[0.25em] uppercase">Will you attend?</Label>
        <RadioGroup value={form.attending} onValueChange={(v) => setField("attending", v)} className="mt-2 flex gap-6" data-testid="rsvp-attending">
          <div className="flex items-center gap-2"><RadioGroupItem value="yes" id="att-yes" data-testid="rsvp-att-yes" /><label htmlFor="att-yes">Yes, I can&apos;t wait</label></div>
          <div className="flex items-center gap-2"><RadioGroupItem value="maybe" id="att-maybe" data-testid="rsvp-att-maybe" /><label htmlFor="att-maybe">Maybe</label></div>
          <div className="flex items-center gap-2"><RadioGroupItem value="no" id="att-no" data-testid="rsvp-att-no" /><label htmlFor="att-no">Regretfully, no</label></div>
        </RadioGroup>
      </div>

      {form.attending !== "no" && event.collect_guest_count && (
        <div>
          <Label className="text-[10px] tracking-[0.25em] uppercase">Number of guests (including you)</Label>
          <Input data-testid="rsvp-guest-count" type="number" min={1} max={20} value={form.guest_count} onChange={(e) => setField("guest_count", e.target.value)} className="rounded-sm mt-1 w-28" />
        </div>
      )}

      {form.attending !== "no" && event.collect_dietary && (
        <div>
          <Label className="text-[10px] tracking-[0.25em] uppercase">Dietary restrictions</Label>
          <Input data-testid="rsvp-dietary" value={form.dietary} onChange={(e) => setField("dietary", e.target.value)} placeholder="Vegetarian, allergies, etc." className="rounded-sm mt-1" />
        </div>
      )}

      {(event.custom_questions || []).map((q) => (
        <div key={q.id}>
          <Label className="text-[10px] tracking-[0.25em] uppercase">{q.label}{q.required && " *"}</Label>
          {q.type === "text" && (
            <Input data-testid={`rsvp-q-${q.id}`} value={answers[q.id] || ""} onChange={(e) => setAns(q.id, e.target.value)} className="rounded-sm mt-1" />
          )}
          {q.type === "dropdown" && (
            <Select value={answers[q.id] || ""} onValueChange={(v) => setAns(q.id, v)}>
              <SelectTrigger data-testid={`rsvp-q-${q.id}`} className="rounded-sm mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{(q.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {q.type === "checkbox" && (
            <div className="mt-2 space-y-2" data-testid={`rsvp-q-${q.id}`}>
              {(q.options || []).map((o) => {
                const checked = Array.isArray(answers[q.id]) && answers[q.id].includes(o);
                return (
                  <label key={o} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={checked} onCheckedChange={() => toggleCheckbox(q.id, o)} /> {o}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      ))}

      <div>
        <Label className="text-[10px] tracking-[0.25em] uppercase">A note for your host</Label>
        <Textarea data-testid="rsvp-message" value={form.message} onChange={(e) => setField("message", e.target.value)} rows={3} className="rounded-sm mt-1" />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 pt-2">
        <Button type="submit" disabled={submitting} data-testid="rsvp-submit" className="rounded-sm w-full sm:w-auto" style={{ background: accent, color: "white" }}>
          {submitting ? "Sending…" : "Send RSVP"}
        </Button>
        <p data-testid="rsvp-privacy-notice" className="text-[11px] leading-snug opacity-70">
          Your reply will be shared with the host and stored by Soirée — see our{" "}
          <a href="/legal/privacy" target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:opacity-100">privacy policy</a>.
        </p>
      </div>
    </form>
  );
}
